import classNames from 'classnames';
import { WithRouterProps } from 'next/dist/client/with-router';
import { SingletonRouter, withRouter } from 'next/router';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';

import { Link } from '../../../../routes';
import StringUtils from '../../utils/StringUtils';

import Merchant from '../../../domain/entities/Merchant';

import css from './MerchantNav.module.scss';

interface RouterProps {
  router?: SingletonRouter;
}

export interface MerchantNavProps {
  booking_page_count: number;
  post_count: number;
  product_count: number;
  merchant: Merchant;
}

type Props = RouterProps &
  InjectedIntlProps &
  WithRouterProps &
  MerchantNavProps;

const MerchantNav = ({
  booking_page_count: bookingPageCount,
  intl,
  post_count: postCount,
  product_count: productCount,
  router,
  merchant,
}: Props) => {
  const setting = merchant.setting;

  // router自体injectされずnullで来る場合がある.
  // このコンポーネントを単体テストする際などに不便なので下記はそのワークアラウンド
  const pathname = router ? router.pathname : '';

  const linkClasses = (paths: string[]) =>
    classNames(
      css.MerchantNav__Link,
      paths.indexOf(pathname) >= 0 ? css.isActive : '',
    );

  const bookingPageNavVisible = bookingPageCount > 0;
  const productPageNavVisible = productCount > 0;
  const postPageNavVisible = postCount > 0;
  const calendarPageNavVisible = !setting.isExploreOnly && bookingPageCount > 0;

  // 他のすべてのliが非表示の場合、「詳細」タブだけ見せるのがデザイン的に良くないので、モバイルでのみ非表示にする
  const hideNavOnMobile = !(
    bookingPageNavVisible ||
    productPageNavVisible ||
    postPageNavVisible ||
    calendarPageNavVisible
  );

  const baseClasses = classNames(css.MerchantNav, {
    [css.MerchantNav__HideOnMobile]: hideNavOnMobile,
  });

  return (
    <ul className={baseClasses}>
      <li className={css.MerchantNav__Item}>
        <Link
          scroll={false}
          href={`/${merchant.publicId}#pageContent`}
          prefetch={false}
        >
          <a className={linkClasses(['/merchants/show'])}>
            {intl.formatMessage({ id: 'merchants.nav.details' })}
          </a>
        </Link>
      </li>
      {bookingPageNavVisible && (
        <li className={css.MerchantNav__Item}>
          <Link
            scroll={false}
            href={`/${merchant.publicId}/booking_pages#pageContent`}
            prefetch={false}
          >
            <a
              className={linkClasses([
                '/merchants/courses',
                '/merchants/resources/show',
              ])}
            >
              {intl.formatMessage({ id: 'merchants.nav.bookingPages' })}
            </a>
          </Link>
        </li>
      )}
      {productPageNavVisible && (
        <li className={css.MerchantNav__Item}>
          <Link
            scroll={false}
            href={`/${merchant.publicId}/products#pageContent`}
            prefetch={false}
          >
            <a
              className={linkClasses([
                '/merchants/products',
                '/merchants/products/show',
              ])}
            >
              {StringUtils.nl2br(
                intl.formatMessage({ id: 'merchants.nav.products' }),
              )}
            </a>
          </Link>
        </li>
      )}
      {postPageNavVisible && (
        <li className={css.MerchantNav__Item}>
          <Link
            scroll={false}
            href={`/${merchant.publicId}/blogs#pageContent`}
            prefetch={false}
          >
            <a
              className={linkClasses([
                '/merchants/blogs',
                '/merchants/blogs/show',
              ])}
            >
              {intl.formatMessage({ id: 'merchants.nav.blogs' })}
            </a>
          </Link>
        </li>
      )}
      {calendarPageNavVisible && (
        <li className={css.MerchantNav__Item}>
          <Link
            scroll={false}
            href={`/${merchant.publicId}/services#pageContent`}
            prefetch={false}
          >
            <a className={linkClasses(['/merchants/services'])}>
              {intl.formatMessage({ id: 'merchants.nav.calendar' })}
            </a>
          </Link>
        </li>
      )}
    </ul>
  );
};

export default injectIntl(withRouter(MerchantNav));
